import React, { useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import EmojiPicker from "emoji-picker-react";
import { useHistory } from "react-router-dom";
import api from "./api";
import BottomNavBar from "./landingPageNew/BottomNavBar";

// Full Quill toolbar configuration (same as lesson builder)
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["code-block"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "color",
  "background",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "align",
  "link",
  "image",
  "video",
  "code-block",
  "script",
  "direction",
];

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px 120px;
  font-family: "Quicksand", sans-serif;
  min-height: 100vh;
`;

const Header = styled.header`
  margin-bottom: 32px;

  h1 {
    margin: 0 0 8px 0;
    font-size: 28px;
    color: #333;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 15px;
  }
`;

const FormCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 8px;
`;

const EmojiPickerContainer = styled.div`
  position: relative;
`;

const EmojiButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 32px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;

  &:hover {
    border-color: #8747d4;
  }

  &:focus {
    outline: none;
    border-color: #8747d4;
  }
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  z-index: 100;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  font-family: "Quicksand", sans-serif;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #8747d4;
  }
`;

const SmallText = styled.small`
  display: block;
  color: #888;
  font-size: 13px;
  margin-top: 6px;
`;

const QuillWrapper = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #8747d4;
  }

  .ql-toolbar {
    border: none !important;
    border-bottom: 2px solid #f0f0f0 !important;
    background: #fafafa;
    padding: 12px !important;
  }

  .ql-container {
    border: none !important;
    font-family: "Quicksand", sans-serif;
    min-height: 250px;
  }

  .ql-editor {
    min-height: 250px;
    font-size: 15px;
    line-height: 1.6;
    padding: 16px;
  }

  .ql-editor.ql-blank::before {
    color: #999;
    font-style: normal;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #8747d4 0%, #a855f7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 14px 28px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  box-shadow: 0 4px 12px rgba(135, 71, 212, 0.3);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(135, 71, 212, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => (props.error ? "#d14343" : "#2f8f2f")};
`;

const PreviewCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  h3 {
    margin: 0 0 16px 0;
    font-size: 18px;
    color: #ff8c69;
    font-weight: 600;
  }
`;

const PreviewContent = styled.div`
  display: flex;
  gap: 14px;
  background: #f9f9f9;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e8e8e8;
`;

const PreviewIcon = styled.div`
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
`;

const PreviewBody = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    margin: 0 0 10px 0;
    font-size: 17px;
    color: #333;
    font-weight: 600;
  }
`;

const PreviewMessage = styled.div`
  color: #555;
  line-height: 1.6;
  font-size: 14px;
  margin-bottom: 12px;
  word-break: break-word;

  p {
    margin: 0 0 8px 0;
  }
`;

const PreviewTarget = styled.small`
  color: #888;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  font-family: "Quicksand", sans-serif;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #8747d4;
    cursor: pointer;
  }
`;

const ViewSentButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 32px;
  background: #fff;
  border: 2px solid #8747d4;
  border-radius: 12px;
  padding: 16px 24px;
  font-weight: 700;
  font-size: 15px;
  color: #8747d4;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  transition: all 0.2s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #8747d4;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(135, 71, 212, 0.3);
  }
`;

const AnnouncementAdmin = () => {
  const history = useHistory();
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState("📢");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [targetEmails, setTargetEmails] = useState("");
  const [message, setMessage] = useState("");
  const [allowReplies, setAllowReplies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleEmojiSelect = (emojiData) => {
    setIcon(emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const emailArray = useMemo(() => {
    if (!targetEmails) return [];
    return targetEmails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => !!e);
  }, [targetEmails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!title.trim() || !message.trim()) {
      setStatus({ type: "error", text: "Title and message are required." });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/api/announcements", {
        title: title.trim(),
        message,
        icon: icon || "📢",
        targetEmails: emailArray,
        allowReplies,
      });
      setStatus({ type: "success", text: "Announcement sent!" });
      setTitle("");
      setMessage("");
      setTargetEmails("");
      setIcon("📢");
      setAllowReplies(false);
    } catch (error) {
      console.error("Failed to create announcement", error);
      setStatus({
        type: "error",
        text: "Failed to send announcement. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Container>
        <Header>
          <h1>Salaam Announcements Admin</h1>
          <p>Create broadcast or targeted announcements with full styling.</p>
        </Header>

        <FormCard>
          <form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Icon</Label>
                <EmojiPickerContainer>
                  <EmojiButton
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    {icon}
                  </EmojiButton>
                  {showEmojiPicker && (
                    <EmojiPickerWrapper>
                      <EmojiPicker
                        onEmojiClick={handleEmojiSelect}
                        width={300}
                        height={400}
                        searchPlaceHolder="Search emoji..."
                        previewConfig={{ showPreview: false }}
                      />
                    </EmojiPickerWrapper>
                  )}
                </EmojiPickerContainer>
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Title *</Label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Announcement title"
                  required
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Target Emails (optional, comma-separated)</Label>
              <Input
                type="text"
                value={targetEmails}
                onChange={(e) => setTargetEmails(e.target.value)}
                placeholder="user1@email.com, user2@email.com"
              />
              <SmallText>
                Leave empty to send to everyone. Currently {emailArray.length}{" "}
                target{emailArray.length === 1 ? "" : "s"}.
              </SmallText>
            </FormGroup>

            <FormGroup>
              <Label>Message * (Full styling available)</Label>
              <QuillWrapper>
                <ReactQuill
                  theme="snow"
                  value={message}
                  onChange={setMessage}
                  modules={modules}
                  formats={formats}
                  placeholder="Write your announcement with full styling..."
                />
              </QuillWrapper>
            </FormGroup>

            <FormGroup>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={allowReplies}
                  onChange={(e) => setAllowReplies(e.target.checked)}
                />
                Allow replies to this message
              </CheckboxRow>
              <SmallText>
                If checked, users will see a text box to reply to this message.
              </SmallText>
            </FormGroup>

            <ButtonRow>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Announcement"}
              </SubmitButton>
              {status && (
                <StatusMessage error={status.type === "error"}>
                  {status.text}
                </StatusMessage>
              )}
            </ButtonRow>
          </form>
        </FormCard>

        <PreviewCard>
          <h3>Preview</h3>
          <PreviewContent>
            <PreviewIcon>{icon || "📢"}</PreviewIcon>
            <PreviewBody>
              <h4>{title || "Announcement title"}</h4>
              <PreviewMessage
                dangerouslySetInnerHTML={{
                  __html: message || "<p>Your message will appear here.</p>",
                }}
              />
              <PreviewTarget>
                {emailArray.length > 0
                  ? `Targeting ${emailArray.length} user${
                      emailArray.length === 1 ? "" : "s"
                    }`
                  : "Broadcast to all"}
              </PreviewTarget>
            </PreviewBody>
          </PreviewContent>
        </PreviewCard>

        <ViewSentButton onClick={() => history.push("/announcement-admin/sent")}>
          View All Salaams Sent
        </ViewSentButton>
      </Container>
      <BottomNavBar />
    </>
  );
};

export default AnnouncementAdmin;
